import { Box, MenuItem, OutlinedInput, TextField, CircularProgress } from "@mui/material";
import {
  BackgroundStyle,
  BigHeading,
  FlexContainer,
  LightBlueContainer,
  ListContainer,
  SearchingBoxContainer,
  LongBlueButton,
  WhiteContainer,
  ReportContainer,
  LabelContainer,
} from "../assets/style";
import RecordTable, { TableRow, TableHeader } from "../components/RecordTable";
import { useEffect, useState } from "react";
import SearchInput from "../components/SearchInput";
import SearchDatePicker from "../components/SearchDatePicker";
import dayjs, { Dayjs } from "dayjs";
import SearchBox from "../components/SearchBox";
import { useNavigate } from "react-router-dom";
import MainContent from "../components/MainContent";
import { entryPageName, reportPageName } from "../components/PageName";
import { useLazyGetScheduleListingQuery } from "../features/schedule/scheduleSlice";
import RecordTableV2 from "../components/RecordTableV2";
import SearchDropDownList from "../components/SearchDropDownList";
import {
  useLazyGetContractDDLQuery,
  useLazyGetContractListQuery,
} from "../features/contract/ContractSlice";
import { ContractItemModel } from "../models/ContractItemModel";
import { ProjectItemModel } from "../models/ProjectItemModel";
import { useLazyGetProjectDDLQuery } from "../features/project/projectSlice";
import { BarChart } from "@mui/x-charts/BarChart";
import FormLabel from "../components/FormLabel";
import FormDropDownList from "../components/FormDropDownList";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useGetLatestAlertQuery } from "../features/dashboard/DashboardSlice";
import LastestAlertCard from "../components/LatestAlertCard";
import AlertByCategoryCard from "../components/AlertByCategoryCard";
import { RecordModel } from "../models/RecordModel";
import { ReportingModel } from "../models/ReportingModel";
import {
  useLazyGetReportQuery,
  useLazyGetWeeklyReportQuery,
  useLazyGetMonthlyReportQuery,
} from "../features/report/reportSlice";
import { useTranslation } from "react-i18next";

const ReportingListPage = () => {
  const { t, i18n } = useTranslation();
  const [getContractDDL] = useLazyGetContractDDLQuery();
  const [getContractList] = useLazyGetContractListQuery();
  const [getReport] = useLazyGetReportQuery();
  const [getWeeklyReport] = useLazyGetWeeklyReportQuery();
  const [getMonthlyReport] = useLazyGetMonthlyReportQuery();
  const [contract, setContract] = useState<ContractItemModel[]>([]);
  const [contractNumber, setContractNumber] = useState("");
  const [contractTitle, setContractTitle] = useState("");
  const [ReportcontractNumber, setReportContractNumber] = useState("");
  const [ReportcontractTitle, setReportContractTitle] = useState("");
  const [ReportcontractId, setReportcontractId] = useState("");
  const [tableRows, setTableRows] = useState<any[]>([]);
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [weeklyStartDate, setWeeklyStartDate] = useState<Dayjs | null>(dayjs());
  const [weeklyEndDate, setWeeklyEndDate] = useState<Dayjs | null>(
    dayjs().add(6, "day")
  );
  const [monthlyDate, setMonthlyDate] = useState<Dayjs | null>(dayjs());
  const { data: LastestAlert } = useGetLatestAlertQuery();
  const [data, setData] = useState<RecordModel<any>>();
  const [AlertData, setAlertData] = useState([]);
  const [series, setSeries] = useState<ReportingModel<number[], string>[]>([]);
  const columns: TableHeader[] = [
    {
      field: "contractNumber",
      headerName: t("Contract No."),
      width: 200,
      isSortable: true,
    },
    {
      field: "contractTitle",
      headerName: t("Contract Title"),
      width: 400,
      isSortable: false,
    },
  ];

  const fetchDataForPage = async (pageNumber: any) => {
    const data = await getContractList({
      ContractName: contractTitle,
      page: pageNumber,
    });
    if (data.status === "fulfilled") {
      return data.data.records.map((item: any) => ({
        id: item?.id,
        contractNumber: item?.number,
        contractTitle: item?.title,
      }));
    }
  };

  useEffect(() => {
    getContractDDL().then((res) => {
      if (res.status == "fulfilled") {
        const contractItems: ContractItemModel[] = res.data.map(
          (item: any) => ({
            id: item.id,
            name: item.name,
            title: item.title,
          })
        );
        const newContractItems: ContractItemModel[] = [
          {
            id: "",
            name: "ALL",
            title: "",
          },
          ...contractItems,
        ];
        setContract(newContractItems);
      }
    });
  }, []);

  const handleSearchClick = () => {
    try {
      getContractList({
        ContractName: contractTitle,
      }).then((res) => {
        if (res.status == "fulfilled") {
          const tableRows = res?.data.records.map((item: any) => ({
            id: item?.id,
            contractNumber: item?.number,
            contractTitle: item?.title,
          }));
          setTableRows(tableRows);
          setData(res?.data);
        }
      });
    } catch (error) {
      console.error("Search error:", error);
    }
  };

  useEffect(() => {
    handleSearchClick();
  }, []);

  const handleContractDDLonChange = (contractNumber: string) => {
    setContractNumber(contractNumber);
    const matchType = contract.find(
      (item) => item.name === contractNumber
    )?.title;
    if (matchType && contractNumber != "ALL") {
      setContractTitle(matchType);
    } else {
      setContractTitle("");
    }
  };

  const handleRowClick = (contract: any) => {
    setReportContractNumber(contract?.contractNumber);
    setReportContractTitle(contract?.contractTitle);
    setReportcontractId(contract?.id);
    try {
      getReport({
        contractId: contract?.id,
        from: date.format("DD/MM/YYYY").toString(),
        to: date.format("DD/MM/YYYY").toString(),
      }).then((res) => {
        if (res.status == "fulfilled") {
          setSeries(res.data?.chartDatas);
          setAlertData(res.data?.alertRecords);
        }
      });
    } catch (e: any) {
      console.error("Error fetching report:", e);
    }
  };

  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [monthlyLoading, setMonthlyLoading] = useState(false);

  const handleWeeklyReport = () => {
    if (!weeklyStartDate || !weeklyEndDate) return;
    setWeeklyLoading(true);
    try {
      getWeeklyReport({
        from: weeklyStartDate.format("YYYY-MM-DD"),
        to: weeklyEndDate.format("YYYY-MM-DD"),
      }).then((res) => {
        if (res.status === "fulfilled") {
          const url = window.URL.createObjectURL(res.data);
          const a = document.createElement("a");
          a.href = url;
          a.download = `weekly_report_${weeklyStartDate.format(
            "YYYYMMDD"
          )}_${weeklyEndDate.format("YYYYMMDD")}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        }
      }).finally(() => {
        setWeeklyLoading(false);
      });
    } catch (e) {
      setWeeklyLoading(false);
      console.error("Error fetching weekly report:", e);
    }
  };

  const handleMonthlyReport = () => {
    if (!monthlyDate) return;
    setMonthlyLoading(true);
    try {
      getMonthlyReport({
        yearstr: monthlyDate.format("YYYY"),
        monthstr: monthlyDate.format("MM"),
      }).then((res) => {
        if (res.status === "fulfilled") {
          const url = window.URL.createObjectURL(res.data);
          const a = document.createElement("a");
          a.href = url;
          a.download = `monthly_report_${monthlyDate.format("YYYYMM")}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        }
      }).finally(() => {
        setMonthlyLoading(false);
      });
    } catch (e) {
      setMonthlyLoading(false);
      console.error("Error fetching monthly report:", e);
    }
  };

  const handleWeeklyStartDateChange = (date: Dayjs | null) => {
    setWeeklyStartDate(date);
    if (date) {
      const maxEndDate = date.add(6, "day");
      if (weeklyEndDate && weeklyEndDate.isAfter(maxEndDate)) {
        setWeeklyEndDate(maxEndDate);
      }
    }
  };

  return (
    <MainContent type={reportPageName}>
      <BackgroundStyle sx={{ zIndex: 0 }}>
        <ListContainer className="data-table-overide-styles">
          <BigHeading>{t("Internal Report")}</BigHeading>
          <SearchingBoxContainer>
            <SearchBox>
              <FlexContainer>
                <SearchDropDownList
                  title={t("Contract")}
                  value={contractNumber}
                  handleValueChange={(event: any) =>
                    handleContractDDLonChange(event?.target?.value)
                  }
                  items={contract.map((item) => item.name)}
                />
              </FlexContainer>
              <FlexContainer>
                <LongBlueButton onClick={handleSearchClick}>
                  {t("Search")}
                </LongBlueButton>
              </FlexContainer>
            </SearchBox>
          </SearchingBoxContainer>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "20px",
            }}
          >
            <div style={{ width: "30%" }}>
              {tableRows && (
                <RecordTableV2
                  columns={columns}
                  rows={tableRows}
                  checkbox={false}
                  pages={data?.totalPages}
                  totalCount={data?.totalCount}
                  onClick={handleRowClick}
                  updateTableRows={setTableRows}
                  fetchDataForPage={fetchDataForPage}
                />
              )}
            </div>
            <div style={{ width: "70%" }}>
              <Box>
                <ReportContainer>
                  <Box marginTop="18px">
                    <FormLabel
                      title={t("Contract No.")}
                      value={ReportcontractNumber}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        gap: 4,
                        margin: "20px 0",
                        padding: "20px",
                        backgroundColor: "#f5f5f5",
                        borderRadius: "8px",
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          padding: "20px",
                          backgroundColor: "white",
                          borderRadius: "8px",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        }}
                      >
                        <LabelContainer
                          style={{
                            marginBottom: "15px",
                            fontSize: "1.1em",
                            fontWeight: "bold",
                          }}
                        >
                          {t("Weekly Report")}
                        </LabelContainer>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label={t("Start Date")}
                            format="MM/DD/YYYY"
                            value={weeklyStartDate}
                            onChange={handleWeeklyStartDateChange}
                            sx={{ width: "100%", marginTop:'15px', marginBottom: "15px" }}
                            slotProps={{
                              textField: { size: "small", color: "primary" },
                            }}
                          />
                          <DatePicker
                            label={t("End Date")}
                            format="MM/DD/YYYY"
                            value={weeklyEndDate}
                            onChange={(date) => setWeeklyEndDate(date)}
                            minDate={weeklyStartDate}
                            maxDate={weeklyStartDate?.add(6, "day")}
                            sx={{ width: "100%", marginBottom: "15px" }}
                            slotProps={{
                              textField: { size: "small", color: "primary" },
                            }}
                          />
                        </LocalizationProvider>
                        <LongBlueButton
                          onClick={handleWeeklyReport}
                          sx={{ width: "100%", marginTop: "10px" }}
                          disabled={weeklyLoading}
                        >
                          {weeklyLoading ? <CircularProgress size={24} /> : t("Generate Weekly Report")}
                        </LongBlueButton>
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          padding: "20px",
                          backgroundColor: "white",
                          borderRadius: "8px",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        }}
                      >
                        <LabelContainer
                          style={{
                            marginBottom: "15px",
                            fontSize: "1.1em",
                            fontWeight: "bold",
                          }}
                        >
                          {t("Monthly Report")}
                        </LabelContainer>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label={t("Select Month")}
                            views={["year", "month"]}
                            format="MM/YYYY"
                            value={monthlyDate}
                            onChange={(date) => setMonthlyDate(date)}
                            sx={{ width: "100%", marginTop:'15px', marginBottom: "15px" }}
                            slotProps={{
                              textField: { size: "small", color: "primary" },
                            }}
                          />
                        </LocalizationProvider>
                        <LongBlueButton
                          onClick={handleMonthlyReport}
                          sx={{ width: "100%", marginTop: "10px" }}
                          disabled={monthlyLoading}
                        >
                          {monthlyLoading ? <CircularProgress size={24} /> : t("Generate Monthly Report")}
                        </LongBlueButton>
                      </Box>
                    </Box>
                  </Box>
                  <Box marginTop="18px">
                    <FormLabel
                      title={t("Contract Title")}
                      value={ReportcontractTitle}
                    />
                  </Box>
                  <Box sx={{ marginTop: "20px" }}>
                    <BarChart
                      series={series}
                      width={1000}
                      height={400}
                      colors={["#EA3354", "#F7CB45", "#5CC8BE"]}
                    />
                  </Box>
                  <Box sx={{ marginTop: "20px" }}>
                    <AlertByCategoryCard data={AlertData} />
                  </Box>
                </ReportContainer>
              </Box>
            </div>
          </div>
        </ListContainer>
      </BackgroundStyle>
    </MainContent>
  );
};
export default ReportingListPage;
